import React from "react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Share2, MessageCircle, Mail, MessageSquare, Send, Facebook, Twitter, Link2 } from "lucide-react";

export default function ShareMenu({ title, price, url, trigger, testid = "share" }) {
  const shareUrl = url || window.location.href;
  const text = `Check out this property on My Property: ${title}${price ? ` - ${price}` : ""}`;
  const enc = encodeURIComponent;
  const full = `${text}\n${shareUrl}`;
  const canNative = typeof navigator !== "undefined" && typeof navigator.share === "function";

  const nativeShare = async (e) => {
    if (e) e.stopPropagation();
    try {
      await navigator.share({ title, text, url: shareUrl });
    } catch (err) { console.debug("native share cancelled/failed", err); }
  };

  const defaultTrigger = (
    <button className="w-8 h-8 rounded-full bg-secondary text-brand-muted hover:text-brand-emerald flex items-center justify-center transition-colors" title="Share">
      <Share2 className="w-4 h-4" />
    </button>
  );

  // PRIMARY: let the OS/browser share sheet handle WhatsApp, WhatsApp Business, and any other app.
  if (canNative) {
    const el = trigger || defaultTrigger;
    return React.cloneElement(el, { onClick: nativeShare, "data-testid": `${testid}-trigger` });
  }

  // FALLBACK (no Web Share API, e.g. most desktop browsers): explicit channels, generic wa.me for WhatsApp.
  const options = [
    { key: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/?text=${enc(full)}` },
    { key: "sms", label: "Message (SMS)", icon: MessageSquare, href: `sms:?&body=${enc(full)}` },
    { key: "email", label: "Email", icon: Mail, href: `mailto:?subject=${enc(title)}&body=${enc(full)}` },
    { key: "telegram", label: "Telegram", icon: Send, href: `https://t.me/share/url?url=${enc(shareUrl)}&text=${enc(text)}` },
    { key: "facebook", label: "Facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${enc(shareUrl)}` },
    { key: "twitter", label: "X (Twitter)", icon: Twitter, href: `https://twitter.com/intent/tweet?text=${enc(text)}&url=${enc(shareUrl)}` },
  ];
  const open = (href) => window.open(href, "_blank", "noopener,noreferrer");
  const copyLink = async () => { try { await navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); } catch (e) { toast.error("Could not copy"); } };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()} data-testid={`${testid}-trigger`}>
        {trigger || defaultTrigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()} className="w-48">
        {options.map((o) => (
          <DropdownMenuItem key={o.key} onClick={() => open(o.href)} data-testid={`${testid}-${o.key}`}>
            <o.icon className="w-4 h-4 mr-2" /> {o.label}
          </DropdownMenuItem>
        ))}
        <DropdownMenuItem onClick={copyLink} data-testid={`${testid}-copy`}><Link2 className="w-4 h-4 mr-2" /> Copy link</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
